import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActivityCard from '../../components/business/ActivityCard';
import Pagination from '../../components/ui/Pagination';
import Loading from '../../components/ui/Loading';
import { useAPI } from '../../hooks/useAPI';
import { getActivities } from '../../services/api/activity';

/**
 * Activities - 交流活动主页面
 * 提供活动列表展示功能
 */
const Activities = () => {
  const { t, i18n } = useTranslation();
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  // 使用useAPI Hook获取活动数据
  const { 
    data: activitiesResponse, 
    loading, 
    error, 
    refetch 
  } = useAPI(() => {
    const params = {
      page: currentPage,
      pageSize
    };
    return getActivities(params);
  }, [currentPage, i18n.language]);

  // 解构活动数据 - 匹配API返回格式
  const activities = activitiesResponse?.records || [];
  const totalItems = activitiesResponse?.total || 0;
  const totalPages = activitiesResponse?.pages || 1;
  const currentPageNum = activitiesResponse?.current || 1;

  // 处理分页
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 活动卡片骨架屏
  const ActivitySkeleton = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse h-full">
      <div className="aspect-w-16 aspect-h-9 bg-gray-200 h-48"></div>
      <div className="p-6 space-y-4">
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            {t('activities.title', '交流活动中心')}
          </h1>
          <p className="text-lg md:text-xl text-primary-100 max-w-3xl mx-auto">
            {t('activities.subtitle', '汇聚版权文化交流活动，促进国际合作与业务发展')}
          </p>
        </div>
      </section>

      {/* 主要内容区 */}
      <main className="container mx-auto px-4 py-8">
        {/* 错误状态 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="text-red-800">
                <span className="text-sm">{error}</span>
              </div>
              <button onClick={refetch} className="text-sm text-red-600 hover:text-red-800 underline">
                重试
              </button>
            </div>
          </div>
        )}

        {/* 加载状态 - 显示骨架屏 */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[...Array(6)].map((_, i) => (
              <ActivitySkeleton key={i} />
            ))}
          </div>
        )}

        {/* 活动列表 */}
        {!loading && activities.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {activities.map((activity) => (
                <ActivityCard
                  key={activity.activityId}
                  activity={{
                    id: activity.activityId,
                    title: activity.title,
                    startDate: activity.startTime,
                    endDate: activity.endTime,
                    coverId: activity.coverId,
                    location: activity.location,
                    organizer: activity.organizer,
                    description: activity.content
                  }}
                  className="h-full"
                />
              ))}
            </div>

            {/* 分页导航 */}
            {totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  currentPage={currentPageNum}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  showQuickJump
                />
              </div>
            )}
          </>
        ) : !loading && (
          /* 空状态 */
          <div className="text-center py-16">
            <h3 className="text-4xl font-medium text-gray-900 mb-2">
              {t('common.comingSoon.title')}
            </h3>
            <p className="text-gray-600">
              {t('common.comingSoon.subtitle')}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Activities; 